/**
 * NeuroTwinPage — „Нервна интелигентност".
 * Периферни нерви и сетивност (симптоми + домашен преглед на стъпалата),
 * автономен баланс (HRV + кортизол + сън) и възбудимост (K, коригиран Ca,
 * Mg, P) — двупосочно: симптомът проверява лабораторията и обратно.
 * Данни: /api/neuro-twin/*
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { twinLabel } from "@/lib/twinCatalog";
import { MobileTopBar } from "@/components/MobileTopBar";
import {
  ArrowLeft, ArrowRight, Activity, AlertTriangle, CheckCircle2, Circle,
  Footprints, Link2, Loader2, Wind,
} from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import PhysiologyBridgeCard from "@/components/PhysiologyBridgeCard";
import PainDiaryCard from "@/components/neuro/PainDiaryCard";
import SensoryPathwayCard from "@/components/neuro/SensoryPathwayCard";
import BalanceFallRiskCard from "@/components/neuro/BalanceFallRiskCard";
import MuscleToneCard from "@/components/neuro/MuscleToneCard";
import VestibularCard from "@/components/neuro/VestibularCard";
import HypothalamusSensesCard from "@/components/neuro/HypothalamusSensesCard";
import HomeSafetyCard from "@/components/neuro/HomeSafetyCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BAND = { green: "#22C55E", yellow: "#EAB308", orange: "#F97316", red: "#EF4444" };
const GROUP_BG = { sensory: "Сетивни", excitability: "Възбудимост", autonomic: "Автономни" };
const SIDES = [{ key: "left", label: "Ляво стъпало" }, { key: "right", label: "Дясно стъпало" }];

const bandColor = (v) => (v >= 75 ? BAND.green : v >= 55 ? BAND.yellow : v >= 35 ? BAND.orange : BAND.red);

export default function NeuroTwinPage() {
  const { isDark } = useTheme();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [examOpen, setExamOpen] = useState(false);
  const [zones, setZones] = useState({});
  const [insp, setInsp] = useState([]);

  const load = () => axios.get(`${API}/neuro-twin/snapshot`)
    .then((r) => setD(r.data))
    .catch(() => toast.error("Не успях да заредя нервния близнак"));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleSymptom = async (key) => {
    const sel = d.symptoms.selected;
    const next = sel.includes(key) ? sel.filter((k) => k !== key) : [...sel, key];
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/neuro-twin/symptoms`, { symptoms: next });
      setD(data);
    } catch { toast.error("Не се записа"); }
    finally { setBusy(false); }
  };

  const saveExam = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/neuro-twin/foot-exam`, {
        zones, inspection: insp,
      });
      setD(data);
      setExamOpen(false);
      toast.success("Прегледът е записан");
    } catch { toast.error("Записът не мина"); }
    finally { setBusy(false); }
  };

  const border = isDark ? "border-white/10" : "border-gray-200";
  const card = isDark ? "bg-[hsl(223,47%,11%)] border border-white/5"
                      : "bg-white border border-gray-100 shadow-sm";

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(222,47%,6%)] text-white" : "bg-[#F8FAFC] text-[#1A1A2E]"}`}
      data-testid="neuro-twin-page">
      <MobileTopBar title="Нервна система" fallbackTo="/dashboard" testid="neuro-twin-mobile-topbar" />
      <SEO title={twinLabel("neuro")}
        description="Периферна невропатия, защитна чувствителност, автономен баланс и възбудимост."
        path="/dashboard/neuro-twin" />
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs font-bold opacity-70 hover:opacity-100"
          data-testid="neuro-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Към таблото
        </Link>

        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold inline-flex items-center gap-2">
              <Activity className="w-6 h-6 text-violet-500" /> {twinLabel("neuro")}
            </h1>
            <p className="text-sm opacity-70 mt-1 max-w-2xl">
              Най-дългите нерви страдат първи. Тук се събират причините, които
              вече са в данните Ви, симптомите и домашният преглед на
              защитната чувствителност.
            </p>
          </div>
          {d?.score != null && (
            <div className="text-right">
              <div className="text-4xl font-extrabold tabular-nums"
                style={{ color: BAND[d.band] }} data-testid="neuro-score">{d.score}</div>
              <div className="text-[11px] opacity-70">от 100</div>
            </div>
          )}
        </div>

        {!d && (
          <div className="mt-8 flex items-center gap-2 text-sm opacity-70">
            <Loader2 className="w-4 h-4 animate-spin" /> Зареждам…
          </div>
        )}

        {d && (
          <>
            {/* активни причини */}
            <div className={`mt-4 p-4 rounded-2xl ${card}`} data-testid="neuro-causes">
              <div className="text-sm font-bold">Кои от Вашите причини са активни</div>
              {d.causes.length === 0 ? (
                <p className="text-[12px] opacity-75 mt-1">
                  В данните Ви няма активна причина за невропатия (HbA1c, eGFR,
                  B12, алкохол, щитовидна жлеза).
                </p>
              ) : (
                <div className="mt-2 space-y-1.5">
                  {d.causes.map((c) => (
                    <div key={c.key} className={`p-2 rounded-lg border ${border}`}
                      data-testid={`neuro-cause-${c.key}`}>
                      <div className="text-[12px] font-bold">{c.label_bg}</div>
                      <div className="text-[11px] opacity-80">{c.why_bg}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* симптоми */}
            <div className={`mt-4 p-4 rounded-2xl ${card}`} data-testid="neuro-symptoms">
              <div className="text-sm font-bold inline-flex items-center gap-1.5">
                Симптоми {busy && <Loader2 className="w-3.5 h-3.5 animate-spin opacity-60" />}
              </div>
              <p className="text-[11px] opacity-70 mt-0.5">
                Отметнете каквото имате. Симптомите на възбудимост се сверяват
                с калий, коригиран калций, магнезий и фосфор.
              </p>
              {["sensory", "excitability", "autonomic"].map((g) => (
                <div key={g} className="mt-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold opacity-50">
                    {GROUP_BG[g]}
                  </div>
                  <div className="mt-1 space-y-1.5">
                    {d.symptoms.catalog.filter((s) => s.group === g).map((s) => {
                      const on = d.symptoms.selected.includes(s.key);
                      const f = (d.excitability.findings || []).find((x) => x.key === s.key);
                      return (
                        <button key={s.key} type="button" onClick={() => toggleSymptom(s.key)}
                          className={`w-full text-left p-2 rounded-lg border transition-colors ${
                            on ? (f?.status === "confirmed"
                              ? "border-rose-500/40 bg-rose-500/10"
                              : "border-violet-500/40 bg-violet-500/10") : border}`}
                          data-testid={`neuro-symptom-${s.key}`}>
                          <div className="text-[12px] font-semibold inline-flex items-center gap-1.5">
                            {on ? <CheckCircle2 className="w-3.5 h-3.5" />
                                : <Circle className="w-3.5 h-3.5 opacity-50" />}
                            {s.label_bg}
                          </div>
                          <div className="text-[10px] opacity-75 mt-0.5 pl-5">{s.why_bg}</div>
                          {on && f?.confirmed_bg && (
                            <div className="text-[10px] font-bold text-rose-500 mt-0.5 pl-5"
                              data-testid={`neuro-symptom-confirmed-${s.key}`}>
                              Потвърдено: {f.confirmed_bg.join(", ")} е нисък
                            </div>
                          )}
                          {on && f?.missing_bg && (
                            <div className="text-[10px] opacity-70 mt-0.5 pl-5">
                              Липсва: {f.missing_bg.join(", ")}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <p className="text-[11px] mt-3 opacity-85" data-testid="neuro-excitability-summary">
                {d.excitability.summary_bg}
              </p>
            </div>

            {/* преглед на стъпалата */}
            <div className={`mt-4 p-4 rounded-2xl ${card}`} data-testid="neuro-foot-card">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-bold inline-flex items-center gap-1.5">
                    <Footprints className="w-4 h-4 text-violet-500" />
                    Домашен преглед на стъпалата
                  </div>
                  {d.foot_exam ? (
                    <p className={`text-[12px] mt-1 ${
                      d.foot_exam.status === "lost" ? "text-rose-500"
                      : d.foot_exam.status === "reduced" ? "text-amber-500" : "opacity-80"}`}
                      data-testid="neuro-foot-status">
                      {d.foot_exam.text_bg} ({d.foot_exam.felt}/{d.foot_exam.total} усетени зони,
                      {" "}{d.foot_exam.date})
                    </p>
                  ) : (
                    <p className="text-[12px] opacity-75 mt-1">
                      Още няма преглед — отнема 3 минути.
                    </p>
                  )}
                  {d.foot_exam?.urgent_bg?.length > 0 && (
                    <p className="text-[12px] font-bold text-rose-500 mt-1 inline-flex items-start gap-1"
                      data-testid="neuro-foot-urgent">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
                      Веднага на лекар: {d.foot_exam.urgent_bg.join(", ")}
                    </p>
                  )}
                </div>
                <button type="button" onClick={() => {
                    if (!examOpen) {
                      // протоколът иска да се маркира само НЕусетеното
                      const init = {};
                      SIDES.forEach((sd) => d.foot_protocol.zones.forEach(
                        (z) => { init[`${sd.key}_${z.key}`] = true; }));
                      setZones(init);
                      setInsp([]);
                    }
                    setExamOpen((v) => !v);
                  }}
                  className="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-[12px] font-semibold"
                  data-testid="neuro-foot-toggle">
                  {examOpen ? "Затвори" : d.foot_exam ? "Нов преглед" : "Направи прегледа"}
                </button>
              </div>

              {examOpen && (
                <div className="mt-3" data-testid="neuro-foot-form">
                  <ol className="text-[11px] opacity-80 space-y-1 list-decimal pl-4">
                    {d.foot_protocol.steps_bg.map((st, i) => <li key={i}>{st}</li>)}
                  </ol>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {SIDES.map((side) => (
                      <div key={side.key} className={`p-2 rounded-lg border ${border}`}>
                        <div className="text-[11px] font-bold mb-1">{side.label}</div>
                        {d.foot_protocol.zones.map((z) => {
                          const id = `${side.key}_${z.key}`;
                          const felt = zones[id] === true;
                          return (
                            <button key={id} type="button"
                              onClick={() => setZones((p) => ({ ...p, [id]: !felt }))}
                              className={`w-full text-left px-2 py-1 rounded-md text-[11px] mb-1 border transition-colors ${
                                felt ? "border-emerald-500/40 bg-emerald-500/10" : border}`}
                              data-testid={`neuro-zone-${id}`}>
                              {felt ? "усещам" : "не усещам"} · {z.label_bg}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                  <div className="mt-3">
                    <div className="text-[11px] font-bold mb-1">Оглед</div>
                    <div className="flex flex-wrap gap-1.5">
                      {d.foot_protocol.inspection.map((i) => {
                        const on = insp.includes(i.key);
                        return (
                          <button key={i.key} type="button"
                            onClick={() => setInsp((p) => on ? p.filter((k) => k !== i.key) : [...p, i.key])}
                            className={`px-2 py-1 rounded-full text-[11px] font-semibold border ${
                              on ? (i.urgent ? "border-rose-500/50 bg-rose-500/15 text-rose-500"
                                             : "border-amber-500/50 bg-amber-500/15") : border}`}
                            data-testid={`neuro-inspection-${i.key}`}>
                            {i.label_bg}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <button type="button" onClick={saveExam} disabled={busy}
                    className="mt-3 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
                    data-testid="neuro-foot-save">
                    {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Запиши прегледа
                  </button>
                </div>
              )}
            </div>

            {/* iter1037 — преглед по пътища + равновесие и риск от падане */}
            <div className="mt-4">
              <SensoryPathwayCard d={d} setD={setD} border={border}
                cardCls={`p-4 rounded-2xl ${card}`} />
            </div>
            <div className="mt-4">
              <BalanceFallRiskCard d={d} setD={setD} border={border}
                cardCls={`p-4 rounded-2xl ${card}`} />
            </div>
            {/* iter1042 — вестибуларен вход и триаж на замайването */}
            <div className="mt-4">
              <VestibularCard d={d} setD={setD} border={border}
                cardCls={`p-4 rounded-2xl ${card}`} />
            </div>
            {/* iter1043 — хипоталамус, сетива и черепни нерви */}
            <div className="mt-4">
              <HypothalamusSensesCard d={d} setD={setD} border={border}
                cardCls={`p-4 rounded-2xl ${card}`} />
            </div>
            {/* iter1038 — план за дома + общ тонусен показател */}
            <div className="mt-4">
              <HomeSafetyCard d={d} setD={setD} border={border}
                cardCls={`p-4 rounded-2xl ${card}`} />
            </div>
            <div className="mt-4">
              <MuscleToneCard d={d} border={border} isDark={isDark}
                cardCls={`p-4 rounded-2xl ${card}`} />
            </div>

            {/* iter1036 — дневник на болката */}
            <div className="mt-4">
              <PainDiaryCard isDark={isDark} cardCls={`p-4 rounded-2xl ${card}`} />
            </div>

            {/* под-оценки */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="neuro-subscores">
              {d.sub_scores.map((s) => (
                <div key={s.key} className={`p-4 rounded-2xl ${card}`}
                  data-testid={`neuro-sub-${s.key}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-bold">{s.title_bg}</div>
                    <div className="text-xl font-extrabold tabular-nums"
                      style={{ color: bandColor(s.score) }}>{s.score}</div>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full overflow-hidden"
                    style={{ background: isDark ? "rgba(255,255,255,.1)" : "rgba(0,0,0,.06)" }}>
                    <div className="h-full rounded-full"
                      style={{ width: `${s.score}%`, background: bandColor(s.score) }} />
                  </div>
                  <p className="text-[12px] opacity-80 mt-2">{s.text_bg}</p>
                  {s.key === "autonomic" && (
                    <p className="text-[11px] mt-2 inline-flex items-start gap-1.5 text-violet-600 dark:text-violet-300"
                      data-testid="neuro-vagal-lever">
                      <Wind className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {d.autonomic.lever_bg}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* връзки */}
            {d.links?.length > 0 && (
              <div className={`mt-4 p-4 rounded-2xl ${card}`} data-testid="neuro-links">
                <div className="text-sm font-bold inline-flex items-center gap-1.5">
                  <Link2 className="w-4 h-4 text-violet-500" />
                  Какво значи това за другите близнаци
                </div>
                <div className="mt-2 space-y-2">
                  {d.links.map((l, i) => (
                    <div key={i} className={`p-3 rounded-xl border ${
                      l.urgency === "high" ? "border-rose-500/40 bg-rose-500/10" : border}`}
                      data-testid={`neuro-link-${l.twin}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        {l.urgency === "high" && <AlertTriangle className="w-3.5 h-3.5 text-rose-500" />}
                        <span className="text-[13px] font-bold">{l.title_bg}</span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-500/15">
                          {l.twin_bg}
                        </span>
                      </div>
                      <p className="text-[12px] opacity-80 mt-1">{l.why_bg}</p>
                      <p className="text-[12px] font-medium mt-1">{l.action_bg}</p>
                      <Link to={l.route}
                        className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-violet-600 dark:text-violet-300"
                        data-testid={`neuro-link-cta-${l.twin}`}>
                        Отвори {l.twin_bg} <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* действия и липси */}
            <div className={`mt-4 p-4 rounded-2xl ${card}`} data-testid="neuro-actions">
              <div className="text-sm font-bold">Следващи стъпки</div>
              <ul className="mt-2 space-y-2">
                {d.actions.map((a, i) => (
                  <li key={i} className="text-[12px]">
                    <span className="font-bold">{a.title_bg}</span>
                    <span className="opacity-80"> — {a.why_bg}</span>
                  </li>
                ))}
              </ul>
            </div>

            {d.gaps?.length > 0 && (
              <div className={`mt-4 p-4 rounded-2xl ${card}`} data-testid="neuro-gaps">
                <div className="text-sm font-bold">Какво не мога да преценя без тези данни</div>
                <ul className="mt-2 space-y-1.5">
                  {d.gaps.map((g) => (
                    <li key={g.key} className="text-[12px]" data-testid={`neuro-gap-${g.key}`}>
                      <span className="font-bold">{g.label_bg}</span>
                      <span className="opacity-80"> — {g.text_bg}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4">
              <PhysiologyBridgeCard focus="nerve" isDark={isDark} cardCls={`p-4 rounded-2xl ${card}`} />
            </div>

            <p className="text-[10px] opacity-50 mt-4">{d.note_bg}</p>
          </>
        )}
      </div>
    </div>
  );
}
